import { FC, useCallback, useEffect, useRef, useState } from "react";
import UnderDevelopment from "components/utils/UnderDevelopment";
import { createIcon } from "components/icons/IconFactory";
import { TabsEnum, useSubContractTabs } from "./hooks/useSubContractTabs";
import { ContractDetailsGlobal } from "api/contract";
import { BranchCode } from "api/risk";
import ContractInfos from "./contract-infos/ContractInfos";
import { ContractInsuranceClaim } from "./contract-insurance-claim/ContractInsuranceClaim";
import { ContractDocuments } from "./contract-documents/ContractDocuments";
import ContractBilling from "./contract-billing/ContractBilling";
import { Loading } from "components";
import NoteManager, { NoteManagerRef } from "features/note-view/components/NoteManager";
import { ActivityTimeline } from "features/activity-feed";

interface SubContractProps {
  contractDetails: ContractDetailsGlobal;
  contractId: string
}

export const SubContract: FC<SubContractProps> = ({ contractDetails, contractId }) => {
  const [loading, setLoading] = useState(false);
  const [pendingAddNote, setPendingAddNote] = useState(false);
  const noteManagerRef = useRef<NoteManagerRef>(null);

  const handleSendMail = useCallback(async () => {
    setLoading(true);
    try {
      // Simuler l'ouverture d'un modal d'envoi d'email
      await new Promise(resolve => setTimeout(resolve, 500));
      console.log('Modal d\'envoi d\'email ouvert pour:', contractDetails);
      // Ici vous pourriez ouvrir un modal d'email
    } catch (error) {
      console.error('Erreur lors de l\'ouverture du modal:', error);
    } finally {
      setLoading(false);
    }
  }, [contractDetails]);

  const handleOpenModal = useCallback((modalConfig: any) => {
    console.log('Ouverture du modal:', modalConfig);
    // Ici vous gérez l'ouverture du modal avec votre système de modals
  }, []);

  const handleShowNotification = useCallback((message: string, type: 'success' | 'error' | 'info') => {
    console.log(`Notification ${type}:`, message);
    // Ici vous afficheriez la notification avec votre système de notifications
  }, []);

  // Handler pour nouvelle note : signaler qu'on veut ouvrir le formulaire
  const handleNewNote = useCallback(() => {
    setPendingAddNote(true);
  }, []);

  const {
    activeTab,
    handleTabChange,
    getTabsByCategory,
    isTabAction,
  } = useSubContractTabs({
    contractDetails,
    documentsCount: 5,
    onNewNote: handleNewNote,
    onSendMail: handleSendMail,
    onOpenModal: handleOpenModal,
    onShowNotification: handleShowNotification,
  });

  // Quand pendingAddNote est true et qu'on est sur l'onglet Notes, ouvrir le formulaire
  useEffect(() => {
    if (pendingAddNote && activeTab === TabsEnum.NOTES && noteManagerRef.current) {
      noteManagerRef.current.openAddForm();
      setPendingAddNote(false);
    }
  }, [pendingAddNote, activeTab]);

  const renderTabContent = () => {
    switch (activeTab) {
      case TabsEnum.CONTRACT:
        return (
          <ContractInfos branchCode={contractDetails.branchCode as BranchCode} contractId={contractId} />
        );

      case TabsEnum.BILLING:
        return (
          <ContractBilling branchCode={contractDetails.branchCode as BranchCode} contractId={contractId} />
        );

      case TabsEnum.DOCUMENTS:
        return (
          <ContractDocuments contractId={contractId} riskId={contractDetails.riskId} contractDetails={contractDetails} />
        );

      case TabsEnum.CLAIMS:
        return (
          <ContractInsuranceClaim contractId={contractId} />
        );

      case TabsEnum.NOTES:
        return (
          <div className="bg-white rounded-xl p-2 shadow-sm border">
            <NoteManager ref={noteManagerRef} riskId={contractDetails.riskId} />
          </div>
        );

      case TabsEnum.COMPLAIN:
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h3 className="text-lg font-semibold mb-4 text-gray-900">Réclamations</h3>
              <UnderDevelopment featureName="Réclamations" />
            </div>
          </div>
        );

      case TabsEnum.HISTORY:
        return (
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <ActivityTimeline riskId={contractDetails.riskId} borrowerProjectId={contractDetails.borrowerProjectId} />
          </div>
        );

      default:
        return (
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <UnderDevelopment featureName="Section contrat" />
          </div>
        );
    }
  };

  const getIcon = useCallback((iconKey: string) => {
    const Icon = createIcon(iconKey);
    return <Icon className="w-5 h-5" />;
  }, []);

  const renderBadge = (badge: string | number) => {
    if (typeof badge === 'number') {
      return (
        <span className="bg-primary/10 text-primary text-xs font-medium px-2.5 py-1 rounded-full border border-primary/20">
          {badge}
        </span>
      );
    }

    return (
      <span className="bg-green-50 text-green-600 text-xs font-medium px-2.5 py-1 rounded-full border border-green-200">
        {badge}
      </span>
    );
  };

  const mainTabs = getTabsByCategory('main');
  const actionTabs = getTabsByCategory('actions');

  return (
    <div className="w-full">
      <Loading loading={loading} isFullPage />
      <div className="flex gap-6">
        {/* Sidebar avec les onglets */}
        <div className="flex-shrink-0 space-y-8 max-w-56">
          {/* Onglets principaux - Style gris clair comme dans le design */}
          <div className="bg-primary/5 rounded-3xl shadow-sm overflow-hidden sticky top-6 border border-primary/10">
            <nav className="p-2 space-y-2">
              {mainTabs.map((tab, index) => (
                <button
                  key={tab.value}
                  onClick={() => !tab.disabled && handleTabChange(tab.value)}
                  disabled={tab.disabled}
                  className={`
                    w-full flex items-center gap-4 px-3 py-3 text-left transition-all duration-300 group
                    ${mainTabs.length - 1 !== index && "border-b "}
                  `}
                >
                  <div className={`flex-shrink-0 transition-all duration-300 ${activeTab === tab.value
                    ? 'text-primary transform scale-110'
                    : tab.disabled
                      ? 'text-gray-300'
                      : 'text-gray-500 group-hover:text-primary group-hover:transform group-hover:scale-110'
                    }`}>
                    {getIcon(tab.icon)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm truncate transition-all duration-300 ${activeTab === tab.value ? 'text-primary font-bold' : 'text-gray-500 font-semibold'
                        }`}>
                        {tab.label}
                      </span>
                      {tab.badge && (
                        <div className={`transition-all duration-300 ${activeTab === tab.value ? 'transform scale-110' : ''
                          }`}>
                          {activeTab === tab.value ? (
                            <span className="bg-primary text-white text-xs font-medium px-2.5 py-1 rounded-full border border-primary/30">
                              {tab.badge}
                            </span>
                          ) : (
                            renderBadge(tab.badge)
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </nav>
          </div>

          {/* Actions rapides - Style blanc avec accent orange */}
          {actionTabs.length > 0 && (
            <div className="space-y-4 max-w-56">
              <h3 className="font-bold text-center text-secondary text-lg px-2">Actions rapides</h3>
              <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden sticky top-80">
                <nav className="p-2 space-y-2">
                  {actionTabs.map((tab, index) => (
                    <button
                      key={tab.value}
                      onClick={() => !tab.disabled && handleTabChange(tab.value)}
                      disabled={tab.disabled}
                      className={`
                        w-full flex items-center gap-4 px-3 py-3 text-left transition-all duration-300 group
                        ${tab.disabled
                          ? 'text-gray-400 cursor-not-allowed opacity-50'
                          : 'text-gray-700'
                        }
                    ${actionTabs.length - 1 !== index && "border-b border-gray-100"}
                        ${isTabAction(tab.value) ? 'cursor-pointer' : ''}
                      `}
                      title={isTabAction(tab.value) ? `Cliquer pour ${tab.label.toLowerCase()}` : tab.description}
                    >
                      <div className={`flex-shrink-0 transition-all duration-300 ${tab.disabled
                        ? 'text-secondary/40'
                        : 'text-secondary bg-secondary/10 rounded-full p-1.5 group-hover:transform group-hover:scale-110'
                        }`}>
                        {getIcon(tab.icon)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-sm truncate transition-all duration-300">
                            {tab.label}
                          </span>
                          {tab.badge && (
                            <div className="transition-all duration-300">
                              {renderBadge(tab.badge)}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          )}
        </div>

        {/* Contenu principal */}
        <div className="flex-1 min-w-0">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};
